// src/components/ImageRateLimitBanner.js
import React, { useEffect, useRef, useState } from 'react';
import { XCircle } from 'lucide-react';

const API_MARKER = '/api/v1/images/';

export default function ImageRateLimitBanner() {
  /* ── UI state ─────────────────────────────────────────────── */
  const [show,  setShow]  = useState(false);
  const [count, setCount] = useState(0);

  /* ── refs survive re‑renders ──────────────────────────────── */
  const confirmed429 = useRef(new Set()); // URLs already flagged
  const checkedURLs  = useRef(new Set()); // URLs probed with HEAD

  useEffect(() => {
    /* ── helper: flag one URL as 429 ─────────────────────────── */
    const flag429 = (src) => {
      if (confirmed429.current.has(src)) return;
      confirmed429.current.add(src);
      setCount(confirmed429.current.size);
      setShow(true);
    };

    /* ── helper: HEAD probe (once per URL) ───────────────────── */
    const probeHead = (src) => {
      if (checkedURLs.current.has(src)) return;
      checkedURLs.current.add(src);
      fetch(src, { method: 'HEAD', cache: 'no-store' })
        .then((r) => r.status === 429 && flag429(src))
        .catch(() => {});
    };

    /* ── wire a single <img> element ─────────────────────────── */
    const wireImg = (img) => {
      if (!img || img.dataset.rlWired) return;
      const src = img.currentSrc || img.src;
      if (!src || !src.includes(API_MARKER) || src.includes('placeholder')) return;

      if (img.complete && img.naturalWidth === 0) probeHead(src);
      img.addEventListener('error', () => probeHead(src));
      img.dataset.rlWired = 'true';
    };

    /* ── observe a document for <img> / <iframe> mutations ──── */
    const observeDoc = (doc) => {
      if (!doc || doc.__rlObserved) return;
      doc.__rlObserved = true;
      doc.querySelectorAll('img').forEach(wireImg);

      const mo = new MutationObserver((muts) =>
        muts.forEach(({ addedNodes }) =>
          addedNodes.forEach((n) => {
            if (n.tagName === 'IMG') wireImg(n);
            else if (n.querySelectorAll) n.querySelectorAll('img').forEach(wireImg);
            if (n.tagName === 'IFRAME') watchFrame(n);
          })
        )
      );
      mo.observe(doc.body || doc, { childList: true, subtree: true });
    };

    /* ── watch an iframe every time it loads / src changes ───── */
    const watchFrame = (frame) => {
      if (!frame) return;
      const tryWire = () => {
        try { observeDoc(frame.contentDocument); } catch {}
      };

      frame.addEventListener('load', tryWire);
      tryWire(); // wire immediately if already loaded

      /* re‑wire on src/srcdoc change */
      const attrWatcher = new MutationObserver(tryWire);
      attrWatcher.observe(frame, { attributes: true, attributeFilter: ['src', 'srcdoc'] });
    };

    /* ── message handler from iframe reporter scripts ────────── */
    const onMessage = (e) => {
      const m = e.data;
      if (m && m.type === 'IMAGE_RATE_LIMIT' && typeof m.src === 'string') {
        flag429(m.src);      // trust iframe report
        // probeHead(m.src); // optional: double‑check from parent
      }
    };
    window.addEventListener('message', onMessage);

    /* ── kick‑off wiring ─────────────────────────────────────── */
    observeDoc(document);                      // top page
    document.querySelectorAll('iframe').forEach(watchFrame);

    /* ── cleanup ─────────────────────────────────────────────── */
    return () => window.removeEventListener('message', onMessage);
  }, []); // helpers live inside; deps array stays empty

  /* ── banner UI ─────────────────────────────────────────────── */
  if (!show) return null;

  return (
    <div className="fixed top-4 right-4 z-[10000] bg-yellow-50 border-l-4 border-yellow-400 p-4 max-w-md shadow-lg rounded">
      <div className="flex">
        <svg className="h-5 w-5 text-yellow-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>

        <div className="ml-3">
          <p className="text-sm font-medium text-yellow-800">Image rate limit reached</p>
          <p className="text-sm text-yellow-700 mt-1">
            {count} {count === 1 ? 'image' : 'images'} couldn’t be loaded (HTTP 429). Please wait a
            moment and try again.
          </p>
        </div>

        <button
          onClick={() => {
            setShow(false);
            setCount(0);
            confirmed429.current.clear();
            checkedURLs.current.clear();
          }}
          className="ml-auto -m-1.5 rounded-md p-1.5 text-yellow-500 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
        >
          <XCircle className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
